import type { IngredientCategory } from "@/lib/types";
import {
  ingredientDraftSchema,
  prepareIngredient,
  sodiumToSaltFraction,
  type IngredientDerivation,
} from "../preparation";
import { kcalPerServingToKcalPerGram, perServingToPct } from "./normalize";
import { nutritionDraftSchema } from "./schema";
import type { NutritionDraft } from "./types";

/** Nutrition labels enter as grams/serving; the prepared composition is always g/g. */
export function prepareNutrition(
  category: IngredientCategory,
  draft: NutritionDraft,
) {
  const parsed = nutritionDraftSchema.safeParse(draft);
  const fraction = (grams: number) =>
    perServingToPct(grams, draft.gramsPerServing) / 100;
  const fatPct = fraction(draft.fatPerServing);
  const carbPct = fraction(draft.carbsPerServing);
  const sugarsPct = fraction(draft.sugarsPerServing);
  const proteinPct = fraction(draft.proteinPerServing);
  const sodiumMgPer100g =
    (draft.sodiumMgPerServing / draft.gramsPerServing) * 100;
  const saltPct = sodiumToSaltFraction(sodiumMgPer100g);
  const estimatedWaterPct = Math.max(
    1 - fatPct - carbPct - proteinPct - saltPct,
    0,
  );
  const waterPct =
    draft.waterMode === "manual"
      ? fraction(draft.waterGrams)
      : estimatedWaterPct;
  const lactosePct =
    category === "dairy"
      ? Math.max(sugarsPct - fraction(draft.addedSugarsPerServing), 0)
      : 0;
  const derivation: IngredientDerivation = {
    sodiumMgPer100g,
    sugarBreakdown: { sucrose: sugarsPct - lactosePct, lactose: lactosePct },
  };
  const input = {
    ...ingredientDraftSchema.parse({ name: "Nutrition label", category }),
    fatPct,
    carbPct,
    sugarsPct,
    proteinPct,
    waterPct,
    lactosePct,
    saturatedFatPct: fraction(draft.saturatedFatPerServing),
    transFatPct: fraction(draft.transFatPerServing),
    fiberPct: fraction(draft.fiberPerServing),
    energyKcal: kcalPerServingToKcalPerGram(
      draft.caloriesPerServing,
      draft.gramsPerServing,
    ),
  };
  if (category === "chocolate") {
    input.cocoaFatPct = fatPct;
    input.cocoaSolidsNonfatPct = Math.max(1 - waterPct - fatPct - sugarsPct, 0);
  }
  const prepared = prepareIngredient(input, derivation);
  if (!parsed.success)
    prepared.issues.push(
      ...parsed.error.issues.map((issue) => ({
        message: issue.message,
        path: issue.path,
      })),
    );
  if (waterPct + fatPct + saltPct + carbPct + proteinPct > 1 + 1e-6)
    prepared.issues.push({
      message: "Total composition exceeds 100%",
      path: [],
    });
  return { ...prepared, estimatedWaterPct, derivation };
}
